/**
 * Design tokens for Travelnest Direct.
 *
 * HUI (`@travelnest/hui`) is the single source of truth for colours, type,
 * spacing, radii and shadows, so the guest site matches the host portal and
 * every other Travelnest surface. This module re-exports HUI's theme and adds
 * the handful of layout tokens the guest site needs that HUI does not define
 * (page container widths, media-query helpers and a z-index scale).
 *
 * Import tokens from here, not from `@travelnest/hui` directly, so a future
 * token change has one place to land.
 */
import { theme as hui } from '@travelnest/hui';

export const theme = hui;

export const { colors, spacing, radii, shadows, typography } = hui;

/**
 * Breakpoints match HUI's grid (`theme.breakpoints`): sm 576, md 768, lg 992,
 * xl 1200. `mq.md` is "at least medium", `mqMax.md` is "narrower than medium".
 */
export const breakpoints = {
	sm: 576,
	md: 768,
	lg: 992,
	xl: 1200,
	xxl: 1440,
} as const;

export type Breakpoint = keyof typeof breakpoints;

const minWidth = (px: number) => `@media (min-width: ${px}px)`;
const maxWidth = (px: number) => `@media (max-width: ${px - 1}px)`;

export const mq: Record<Breakpoint, string> = {
	sm: minWidth(breakpoints.sm),
	md: minWidth(breakpoints.md),
	lg: minWidth(breakpoints.lg),
	xl: minWidth(breakpoints.xl),
	xxl: minWidth(breakpoints.xxl),
};

export const mqMax: Record<Breakpoint, string> = {
	sm: maxWidth(breakpoints.sm),
	md: maxWidth(breakpoints.md),
	lg: maxWidth(breakpoints.lg),
	xl: maxWidth(breakpoints.xl),
	xxl: maxWidth(breakpoints.xxl),
};

/** The widest the page content gets at each breakpoint. */
export const containerWidths = {
	lg: '960px',
	xl: '1140px',
	xxl: '1280px',
} as const;

/** Side gutter between the page content and the viewport edge. */
export const pageGutter = {
	mobile: spacing.space40,
	desktop: spacing.space60,
} as const;

/** One scale for everything that stacks, so overlays never fight each other. */
export const zIndex = {
	stickyBar: 100,
	header: 200,
	overlay: 1000,
	modal: 1010,
	toast: 1100,
} as const;

/**
 * Typography tokens as CSS. HUI's `theme.typography.*` entries are objects of
 * CSS properties; spreading them in Emotion template strings needs them as a
 * string, which this gives you: `${font('IR100')}`.
 */
type FontToken = {
	fontFamily: string;
	fontWeight: string;
	fontSize: string;
	letterSpacing: string;
};

/** The typography tokens that describe a font (HUI also stores icon sizes under `typography`). */
export type TypographyToken = {
	[K in keyof typeof typography]: (typeof typography)[K] extends FontToken
		? K
		: never;
}[keyof typeof typography];

export const font = (token: TypographyToken, lineHeight = '150%') => {
	const t = typography[token] as FontToken;
	return `
		font-family: ${t.fontFamily};
		font-weight: ${t.fontWeight};
		font-size: ${t.fontSize};
		letter-spacing: ${t.letterSpacing};
		line-height: ${lineHeight};
	`;
};

/** Headings are Raleway (RWB), set tighter than body copy. */
export const headingFont = (token: TypographyToken) => font(token, '125%');

/** Shared focus ring for anything keyboard-focusable that is not an HUI control. */
export const focusRing = `
	outline: 2px solid ${colors.BB80};
	outline-offset: 2px;
`;
